import joi from 'joi';
import { MenuItem } from "@material-ui/core"
import React from "react";
import _ from "lodash";
import dayjs, {Dayjs} from "dayjs";
import isToday from "dayjs/plugin/isToday";

dayjs.extend(isToday);

// @ts-ignore
const TempStars = window.TempStars;

export const roles = [
    {value: '0', text: 'Registered Dental Hygienist'},
    {value: '1', text: 'Dental Assistant'},
    {value: '2', text: 'Reception/Admin'}
]

// NEW: Function to get roles based on country
export const getRolesForCountry = (country: number) => {
    const baseRoles = [
        {value: '0', text: 'Registered Dental Hygienist'},
        {value: '1', text: 'Dental Assistant'},
    ];
    
    // Only add admin option for Canadian offices
    if (country === Country.Canada) {
        baseRoles.push({value: '2', text: 'Reception/Admin'});
    }
    
    return baseRoles;
}

export const paymentTermsOpts = [
    {id: 1, text:  'Within 24h'},
    {id: 5, text:  'Within 5 Days'},
    {id: 14, text: 'Within 2 Weeks'},
    {id: 30, text: 'Within 1 Month'},
]

export const paymentTermsOpts123Dentist = [
    {id: 1, text:  'Within 24h paid by Corporate'},
]

export const paymentMethodOpts = (country: number, is123Dentist: boolean=false) => {
    if(is123Dentist){
        return [
            {id: 1, text: "Pay Now (TempStars' Payment Processor)"},
        ]
    }
    return [
        {id: 1, text: "Pay Now (TempStars' Payment Processor)"},
        {id: 2, text: country ? 'Check' : 'Cheque'},
        {id: 3, text: 'Electronic Funds Transfer'},
        {id: 4, text: 'Payroll / Temporary Employee'}
    ]
}

export enum Country {
    Canada = 0,
    USA = 1
}

export const JOB_STATUSES = {
    POSTED: 1,
    PARTIAL: 2,
    CONFIRMED: 3,
    COMPLETED: 4,
    OFFER_EXPIRED: 5
}

const ppeErrorMessage = 'To post your shift, please confirm your office complies with workplace safety and infection control regulations.'

/** Minimum allowed temping shift length (T2-5445). Keep in sync with API minShiftDuration util. */
export const MIN_SHIFT_DURATION_HOURS = 3;
export const MIN_SHIFT_DURATION_ERROR = 'Shift must be 3h or more';

/** T2-5444: optional first patient time — keep caution copy in sync with API firstPatientTime util. */
export const FIRST_PATIENT_BUFFER_CAUTION =
    'Best practice is at least a 15-minute buffer between shift start and first patient time.';
export const FIRST_PATIENT_BEFORE_START_ERROR =
    'First patient time cannot be before shift start';

export const isBelowMinShiftDuration = (from: string | Date | number, to: string | Date | number): boolean => {
    const start = new Date(from).getTime();
    const end = new Date(to).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return false;
    return (end - start) < MIN_SHIFT_DURATION_HOURS * 60 * 60 * 1000;
};

export const isFirstPatientTimeBeforeStart = (
    firstPatientTime: string | Date | number | null | undefined,
    from: string | Date | number,
): boolean => {
    if (firstPatientTime == null || firstPatientTime === '') return false;
    const fpt = new Date(firstPatientTime).getTime();
    const start = new Date(from).getTime();
    if (Number.isNaN(fpt) || Number.isNaN(start)) return false;
    return fpt < start;
};

export const isFirstPatientTimeEqualToStart = (
    firstPatientTime: string | Date | number | null | undefined,
    from: string | Date | number,
): boolean => {
    if (firstPatientTime == null || firstPatientTime === '') return false;
    const fpt = new Date(firstPatientTime).getTime();
    const start = new Date(from).getTime();
    if (Number.isNaN(fpt) || Number.isNaN(start)) return false;
    return fpt === start;
};

const adminTasksSchema = joi.object({
    answerPhone: joi.boolean(),
    takePayments: joi.boolean(),
    outboundPatientCalls: joi.boolean(),
    hygieneCoordinating: joi.boolean(),
    checkinPatients: joi.boolean(),
    seatPatients: joi.boolean(),
    stericentre: joi.boolean(),
    floater: joi.boolean(),
});

export const schema = joi
    .object({
        country: joi.number().label('Country'),
        role: joi.number().label('Role').required(),
        location: joi.number().label('Location').required(),
        startDate: joi.any().custom((value: Dayjs, helper: any) => {
            if (!dayjs(value).isValid())
                return helper.message('Start Date is required.');
            return value && (value.isAfter(dayjs()) || value.isToday()) ? value : helper.message('Please select today’s date or a future date for the Start Date');
        })
            .label('Start Date').required(),
        from: joi.date().label('From').required(),
        to: joi.date()
            .greater(joi.ref('from'))
            .custom((value: Date, helper: any) => {
                const from = helper.state.ancestors[0]?.from;
                if (from && isBelowMinShiftDuration(from, value)) {
                    return helper.message(MIN_SHIFT_DURATION_ERROR);
                }
                return value;
            })
            .label('To')
            .messages({
                'date.greater': 'End Time must be later than Start Time',
            })
            .required(),
        // T2-5444: optional; cannot be before start (equal allowed — FE shows non-blocking caution)
        firstPatientTime: joi.alternatives().try(
            joi.date().min(joi.ref('from')).messages({
                'date.min': FIRST_PATIENT_BEFORE_START_ERROR,
            }),
            joi.valid(null, ''),
        ).optional().allow(null, ''),
        proposedRate: joi.number().label('Proposed Rate').required(),
        paymentTerms: joi.number().label('Payment Terms').required(),
        paymentMethod: joi.number().label('Payment Method').required(),
        // NEW: Admin-specific fields
        adminTasks: joi.any().when('role', {
            is: 2,
            then: adminTasksSchema.required().custom((value, helpers) => {
                if (Object.values(value).every(v => v === false)) {
                    return helpers.message({ 'any.custom': 'At least one task must be selected' });
                }
                return value;
            }),
            otherwise: joi.any().strip()
        }),
        
        attire: joi.string().valid('business', 'scrubs', 'either').when('role', {
            is: 2, 
            then: joi.required(),
            otherwise: joi.any().strip()
        }),
        
        // Update existing validations to exclude admin role
        skills: joi.string().label('Skills').when('role', {
            is: 0, 
            then: joi.string(),
            otherwise: joi.any().strip()
        }),
        instaBook: joi.boolean().label('Insta Book'),
        downtimeUnpaid: joi.boolean().label('Downtime Unpaid').when('role', [
            { is: '0', then: joi.boolean().label('Downtime Unpaid') },
            { is: '1', then: joi.forbidden() }
        ]),
        boost: joi.boolean().label('Boost This Shift'),
        paysW2: joi.number().label('Working Relationship').when('country', [
            { is: 0, then: joi.forbidden() },
            { is: 1, then: joi.number().required() }
        ]),
        level: joi.number().label('Level').when('country', [{ is: 1, then: joi.forbidden() },
            { is: 0, then: joi.number().when('role', [{ is: '1', then: joi.number().required() },
                    { is: '0', then: joi.forbidden() }]) }]),
        ppe: joi.boolean().label('PPE').valid(true)
            .messages({
                'any.only': ppeErrorMessage,
                'any.required': ppeErrorMessage,
            }).required(),
        allowPartialOffers: joi.boolean().label('Allow Partial Offers')
    })
    .required()

export const timePickerOptions = (date?: string | number | Date, from = 5, to = 23, step = 15, minTime?: Date) => {
    let timeArray: Date[] = [];
    if (date instanceof Date && from <= to && to <= 23 && from >= 5 && Number.isInteger(60 / step)) {
        const startDate = new Date(date);
        startDate.setHours(from, 0, 0, 0);

        for (let hoursIterator = from; hoursIterator <= to; hoursIterator++) {
            for (let minutesIterator = 0; minutesIterator < 60; minutesIterator += step) {
                const currentDate = new Date(startDate);
                currentDate.setHours(hoursIterator, minutesIterator);
                timeArray.push(currentDate);
            }
        }
    }
    if (minTime) {
        timeArray = timeArray.filter(time => time >= minTime);
    }
    return timeArray.map((time, index) => (
        <MenuItem key={index} value={time.toString()}>
            {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
        </MenuItem>
    ));
};


export const PROPOSED_RATE_RANGES = {
    hygienist: { min: 40, max: 90, default: 50 },
    assistant: { min: 17, max: 60, default: 25 },
} as const;

export const clampProposedRate = (rate: number | undefined, jobType: 0 | 1): number => {
    const range = jobType === 1 ? PROPOSED_RATE_RANGES.assistant : PROPOSED_RATE_RANGES.hygienist;
    if (!rate || rate < range.min || rate > range.max) return range.default;
    return rate;
};

export const rateOptions = (jobType: 0 | 1 | 2, defaultRate?: number) => {
    const getArrayInRange = (min: number, max: number) => _.range(min, max + 1)
    const effectiveType: 0 | 1 = (jobType === 2) ? 1 : jobType;
    const range = effectiveType === 1 ? PROPOSED_RATE_RANGES.assistant : PROPOSED_RATE_RANGES.hygienist;
    defaultRate = clampProposedRate(defaultRate, effectiveType);

    return getArrayInRange(range.min, range.max).map((rate) => {
        return (
            <MenuItem
                key={rate}
                className={'green-rate'}
                value={rate}
                selected={rate === defaultRate}
            >
                {`$${rate}/hr`}
            </MenuItem>
        );
    });
}

export const afterPostJobActions = (postedJob: any, jobData: any, countryCode?: number, diagnostic: Record<string, unknown> = {}) => {
    let shiftPaymentTerms = '<1 Month';
    if (jobData.job.paymentTerms === 1){
        shiftPaymentTerms = '<24 Hours'
    }
    if (jobData.job.paymentTerms === 5) {
        shiftPaymentTerms = '<5 Days'
    }
    if (jobData.job.paymentTerms === 14) {
        shiftPaymentTerms = '<2 Weeks'
    }

    let shiftPaymentMethod = 'Pay Now (TempStars\'s Payment Processor)';
    if (jobData.job.paymentMethod === 2) {
        shiftPaymentMethod = Number(countryCode || 0) === 0 ? 'Cheque' : 'Check';
    } else if (jobData.job.paymentMethod === 3) {
        shiftPaymentMethod = 'Electronic Funds Transfer';
    } else if (jobData.job.paymentMethod === 4) {
        shiftPaymentMethod = 'Payroll / Temporary Employee';
    }


    let dentalWorkerType = 'Hygienist';
    if (jobData.job.type === 1) {
        dentalWorkerType = 'Assistant';
    } else if (jobData.job.type === 2) {
        dentalWorkerType = 'Admin';  // NEW
    }

    TempStars.Analytics.track( 'post shift', {
        ...diagnostic,
        job_id: postedJob.jobId,
        shiftId: postedJob.shiftId,
        isInstabookEnabled: !!jobData.job.Instabook,
        recommendedHourlyRate: parseInt(jobData.job.proposedRate),
        currencyCode: Number(countryCode || 0) === 0 ? 'CAD' : 'USD',
        shiftStartDate: dayjs(jobData.job.from).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        shiftEndDate: dayjs(jobData.job.to).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        dentalWorkerType,
        shiftPaymentTerms,
        shiftPaymentMethod,
        dentalOfficeLocationId: jobData.job.locationId,
        location_id: String(jobData.job.locationId),
        office: [String(jobData.job.locationId)],
        // NEW: Admin-specific analytics
        ...(jobData.job.type === 2 && {
            adminTasks: jobData.adminTasks,
            attire: jobData.attire
        })
    } );
}
